/* eslint-disable @next/next/no-img-element */
import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useRecoilValue } from "recoil";
import clsx from "clsx";
import Tippy from "@tippyjs/react";
import { AnimatePresence } from "framer-motion";

import "tippy.js/dist/tippy.css";

import { engageState } from "../../atoms/platform";
import { apiGetAgnedaPdfs } from "../../lib/api/engage";

import { XIcon, DownloadIcon } from "@heroicons/react/outline";
import { MdOndemandVideo } from "react-icons/md";
import { IoChevronBack } from "react-icons/io5";
import { HiOutlineDocumentDuplicate } from "react-icons/hi";

import Loading from "../Shared/Loading";
import Supplemental from "./Supplemental";
import SupplementalMobile from "./SupplementalMobile";
import AdobeViewer from "../Pdf/AdobeViewer";

const Agenda = () => {
  const router = useRouter();
  const { client, agenda } = router.query;
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [supplemental, setSupplemental] = useState({
    visible: false,
    meeting: null,
  });
  const [mobileSupplemental, setMobileSupplemental] = useState({
    visible: false,
    meeting: null,
  });
  const platform = useRecoilValue(engageState);

  useEffect(() => {
    async function fetchAgenda() {
      setLoading(true);
      const result = await apiGetAgnedaPdfs(agenda);
      if (result) {
        console.log(result);
        setData(result);
        setLoading(false);
      } else {
        setError(true);
      }
    }
    if (agenda) {
      fetchAgenda();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agenda]);

  return (
    <div className="pb-sm antialiased">
      {data && (
        <div
          className={clsx(
            "hidden sm:block max-w-full 2xl:max-w-7/8 mx-auto px-4 sm:px-6 md:px-8 pt-6 bg-white z-40"
          )}
        >
          <div className="pb-2 border-b border-gray-400 flex flex-col items-end mt-2">
            {/* <div className="col-span-2 2xl:col-span-2 flex flex-col 2xl:items-center items-end justify-end"></div> */}
            <div className="mt-4 lg:mt-0 flex justify-end items-end space-x-2 col-span-4 2xl:col-span-1">
              <div className="flex items-center space-x-2 mr-2">
                {platform?.allowSupplemental &&
                  data?.attachments?.length > 0 && (
                    <Tippy
                      content="Supplemental Materials"
                      placement="bottom"
                      animation="scale"
                      delay={[500, 0]}
                    >
                      <div
                        className="flex px-2 py-0.5 md:px-4 md:py-1 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200 focus:outline-none"
                        onClick={() =>
                          setSupplemental({
                            visible: true,
                            meeting: data,
                          })
                        }
                      >
                        <HiOutlineDocumentDuplicate className="h-4 w-4 md:h-6 md:w-6 xl:h-8 xl:w-8 text-blueGray-200 cursor-pointer p-0 md:p-1" />
                      </div>
                    </Tippy>
                  )}

                {data?.videoUrl && (
                  <Tippy
                    content="Watch Video of the Meeting"
                    placement="bottom"
                    animation="scale"
                    delay={[500, 0]}
                  >
                    <div>
                      <a
                        href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${data?.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex px-2 py-0.5 sm:px-4 md:py-1 sm:py-2 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200 focus:outline-none"
                        title="Watch Video"
                        role="button"
                        aria-label="Open Webpage with Video"
                      >
                        <MdOndemandVideo className="h-4 w-4 md:h-6 md:w-6 xl:h-8 xl:w-8 text-blueGray-200 cursor-pointer p-0 md:p-1" />
                      </a>
                    </div>
                  </Tippy>
                )}

                {data?.agendaPdfUrl &&
                  data?.publishStatus !== "draftreview" && (
                    <Tippy
                      content="Download Agenda"
                      placement="bottom"
                      animation="scale"
                      delay={[500, 0]}
                    >
                      <div>
                        <a
                          href={data?.agendaPdfUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="flex px-2 py-0.5 sm:px-4 md:py-1 sm:py-2 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200 focus:outline-none"
                          title="Download Agenda Packet"
                          role="button"
                          aria-label="Download Agenda Packet"
                        >
                          <DownloadIcon className="h-4 w-4 md:h-6 md:w-6 xl:h-8 xl:w-8 text-blueGray-200 cursor-pointer p-0 md:p-1" />
                        </a>
                      </div>
                    </Tippy>
                  )}
                {data?.agendaPacketUrl &&
                  data?.publishStatus !== "draftreview" && (
                    <Tippy
                      content="Download Agenda Packet"
                      placement="bottom"
                      animation="scale"
                      delay={[500, 0]}
                    >
                      <div>
                        <a
                          href={data?.agendaPacketUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm md:text-base flex px-2 py-0.5 sm:px-4 md:py-1 sm:py-1.5 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200focus:outline-none"
                          title="Download Agenda Packet"
                          role="button"
                          aria-label="Download Agenda Packet"
                        >
                          <DownloadIcon className="h-4 w-4 md:h-6 md:w-6 xl:h-8 xl:w-8 text-blueGray-200 cursor-pointer p-0 md:p-1 mr-1" />
                          Packet
                        </a>
                      </div>
                    </Tippy>
                  )}
                {data?.minutesPdfUrl && (
                  <Tippy
                    content="Download Agenda Packet"
                    placement="bottom"
                    animation="scale"
                    delay={[500, 0]}
                  >
                    <div>
                      <a
                        href={data?.minutesPdfUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm md:text-base flex px-2 py-0.5 sm:px-4 md:py-1 sm:py-1.5 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200focus:outline-none"
                        title="Download Agenda Packet"
                        role="button"
                        aria-label="Download Agenda Packet"
                      >
                        <DownloadIcon className="h-4 w-4 md:h-6 md:w-6 xl:h-8 xl:w-8 text-blueGray-200 cursor-pointer p-0 md:p-1 mr-1" />
                        Minutes
                      </a>
                    </div>
                  </Tippy>
                )}
              </div>
              <XIcon
                className="h-6 w-6 xl:h-8 xl:w-8 2xl:h-10 2xl:w-10 text-gray-800 cursor-pointer hover:text-delete-btn transform duration-200 hover:rotate-180"
                aria-hidden="true"
                onClick={() => router.back()}
              />
            </div>
          </div>
        </div>
      )}

      {/* Mobile header */}
      {data && (
        <div className="sm:hidden px-4 sm:px-6 pt-2 bg-white">
          <div className="pb-1 border-b border-gray-400">
            <div className="relative flex items-center justify-center">
              <h3 className="text-sm lg:text-lg xl:text-2xl leading-6 font-medium text-gray-900">
                {data?.department?.name}
              </h3>
              <IoChevronBack
                className="absolute top-1 left-0 h-6 w-6 text-gray-800 cursor-pointer hover:text-gray-400"
                aria-hidden="true"
                onClick={() => router.back()}
              />
            </div>

            <div className="mt-2 flex justify-end items-center space-x-2">
              {platform?.allowSupplemental && data?.attachments?.length > 0 && (
                <Tippy
                  content="Supplemental Resources"
                  placement="bottom"
                  animation="scale"
                  delay={[500, 0]}
                >
                  <div>
                    <div
                      className="flex px-2 py-0.5 rounded-md items-center justify-evenly text-black focus:outline-none"
                      onClick={() => {
                        setMobileSupplemental({
                          visible: true,
                          meeting: data,
                        });
                      }}
                    >
                      <HiOutlineDocumentDuplicate className="h-5 w-5" />
                    </div>
                  </div>
                </Tippy>
              )}

              {data?.videoUrl && (
                <Tippy
                  content="Watch Video of the Meeting"
                  placement="bottom"
                  animation="scale"
                  delay={[500, 0]}
                >
                  <div>
                    <a
                      href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${data?.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex px-2 py-0.5 rounded-md items-center justify-evenly text-black focus:outline-none"
                      title="Watch Video"
                      role="button"
                      aria-label="Open Webpage with Video"
                    >
                      <MdOndemandVideo className="h-5 w-5" />
                    </a>
                  </div>
                </Tippy>
              )}
              {data?.agendaPdfUrl && data?.publishStatus !== "draftreview" && (
                <Tippy
                  content="Download Agenda"
                  placement="bottom"
                  animation="scale"
                  delay={[500, 0]}
                >
                  <div>
                    <a
                      href={data?.agendaPdfUrl}
                      className="flex px-2 py-0.5 rounded-md items-center justify-evenly text-black focus:outline-none"
                      target="_blank"
                      rel="noreferrer"
                      title="Download Agenda Packet"
                      role="button"
                      aria-label="Download Agenda Packet"
                    >
                      <DownloadIcon className="h-5 w-5" />
                    </a>
                  </div>
                </Tippy>
              )}
              {data?.agendaPacketUrl && data?.publishStatus !== "draftreview" && (
                <Tippy
                  content="Download Agenda Packet"
                  placement="bottom"
                  animation="scale"
                  delay={[500, 0]}
                >
                  <div>
                    <a
                      href={data?.agendaPacketUrl}
                      className="flex px-2 py-0.5 rounded-md items-center justify-evenly text-black focus:outline-none"
                      target="_blank"
                      rel="noreferrer"
                      title="Download Agenda Packet"
                      role="button"
                      aria-label="Download Agenda Packet"
                    >
                      <DownloadIcon className="h-5 w-5 mr-1" />
                      Packet
                    </a>
                  </div>
                </Tippy>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Agenda Details */}
      {data && (
        <div className="max-w-full 2xl:max-w-7/8 mx-auto px-4 sm:px-6 md:px-8">
          <div className="h-screen w-full">
            <AdobeViewer url={data?.agendaPdfUrl} fileName={`Agenda.pdf`} />
          </div>
        </div>
      )}

      {supplemental?.visible && (
        <Supplemental
          meeting={supplemental?.meeting}
          update={setSupplemental}
        />
      )}

      <AnimatePresence>
        {mobileSupplemental?.visible && (
          <SupplementalMobile
            meeting={mobileSupplemental?.meeting}
            update={setMobileSupplemental}
          />
        )}
      </AnimatePresence>

      {loading && <Loading />}
      {error && (
        <div className="fixed z-10 inset-0 overflow-hidden">
          <div className="relative flex flex-col min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <img
              src="/images/jmCircle.svg"
              className="h-12 2xl:h-32 w-auto absolute top-4 left-4 z-50"
              alt="justMeetings logo"
            />
            <div className="fixed inset-0 bg-coolGray-900 transition-opacity"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
              &#8203;
            </span>
            {/* Start Modal */}
            <div className="inline-block bg-coolGray-900 rounded-lg text-left transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:px-6 sm:pt-2 sm:pb-8">
              <div className="grid grid-cols-1 gap-2 my-10">
                <div>
                  <div className="mt-3 text-center">
                    <h3
                      className="text-lg 2xl:text-4xl leading-6 font-bold text-blueGray-200"
                      id="modal-title"
                    >
                      Invalid Meeting
                    </h3>
                    <div className="mt-2 xl:mt-8">
                      <p className="text-lg text-blueGray-300 mb-2 text-center font-medium">
                        Your url is using{" "}
                        <span className="font-bold text-white">{agenda}</span>,
                        which is not a valid meeting within JustMeetings.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* End Modal */}
          </div>
        </div>
      )}
      {data?.publishStatus === "draft" && (
        <div className="absolute top-0 left-0 bg-slate-700 h-screen w-full z-50 flex items-center justify-center">
          <p className="text-lg 2xl:text-2xl font-bold text-white">
            This agenda is currently unavailable
          </p>
        </div>
      )}
    </div>
  );
};

export default Agenda;
